<template>
  <ChartContainer :title="title" class="partners-chart-container">
    <div class="partners-chart">
      <div class="partners-chart__title flex align-center justify-between">
        <h4>{{ currentPage === 1 ? `ТОП-${partnersLimit} партнерів за прибутком`: '' }}</h4>
        <p class="partners-limit">{{ getPartnersCount }} точок продажу</p>
      </div>
      <div class="partners-chart__chart">
        <div class="partners-chart__chart-bars">
          <div
            v-for="partner in sortedPartners"
            :key="partner.id"
            class="partners-chart__chart-bar"
            :style="{ width: getSquareRootBarWidth(partner.value) }"
          >
            <p class="partner-name">{{ partner.name }}</p>
            <p class="partner-sales">{{ formatBalance(partner.value, calculateAffix(type).prefix, calculateAffix(type).suffix) }}</p>
          </div>
        </div>
        <Paginator
          v-if="!!getPartnersCount"
          class="partners-chart-paginator"
          :hide-if-one-page="false"
          :current-page="currentPage"
          :page-size="partnersLimit"
          :items-count="getPartnersCount"
          @page-changed="paginate"
        />
      </div>
      <slot></slot>
    </div>
  </ChartContainer>
</template>

<script>
import { balanceFormatter } from '@/shared/lib/balance';
import ChartContainer from '@/shared/ui/chart/ui/chart-container/index.vue';
import { Paginator } from '@/shared/ui/paginator';

export default {
  props: {
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      required: true,
    },
    partners: {
      type: Array,
      default: () => [],
    },
    partnersLimit: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      currentPage: 1,
      containerWidth: 0,
      resizeObserver: null
    };
  },
  created() {
    this.pageSize = this.partnersLimit;
    this.itemsCount = this.partners.length;
  },
  mounted() {
    this.measureContainer();
    this.setupResizeObserver();
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  mixins: [ balanceFormatter ],
  components: { ChartContainer, Paginator },
  computed: {
    getPartnersCount() {
      return this.partners.length;
    },
    sortedPartners() {
      const partnersCopy = [...this.partners];
      const sortedPartners = partnersCopy.sort((a, b) => b.value - a.value);
      const start = (this.currentPage - 1) * this.partnersLimit;
      const end = start + this.partnersLimit;

      return sortedPartners.slice(start, end);
    },
    maxValue() {
      if (this.partners.length === 0) {
        return 0;
      }
      return Math.max(...this.partners.map((partner) => partner.value));
    },
  },
  methods: {
    measureContainer() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.partners-chart__chart-bars');
        if (container) {
          this.containerWidth = container.clientWidth;
        }
      });
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.containerWidth = entry.contentRect.width;
        }
      });
      const container = this.$el.querySelector('.partners-chart__chart-bars');
      if (container) {
        this.resizeObserver.observe(container);
      }
    },
    getMinWidthPercentage() {
      if (!this.containerWidth || this.containerWidth <= 200) return 100;
      return (200 / this.containerWidth) * 100;
    },
    getLinearBarWidth(value) {
      if (this.maxValue === 0) return '100%';

      const minWidthPercentage = this.getMinWidthPercentage();
      const rawPercentage = (value / this.maxValue) * 100;
      const percentage = minWidthPercentage + (rawPercentage * (100 - minWidthPercentage) / 100);

      return `${Math.max(percentage, minWidthPercentage)}%`;
    },
    getLogarithmicBarWidth(value) {
      if (this.maxValue <= 0) return '100%';
      if (value <= 0) return this.getMinWidthPercentage() + '%';

      const minWidthPercentage = this.getMinWidthPercentage();
      const logValue = Math.log10(value + 1);
      const logMax = Math.log10(this.maxValue + 1);
      const rawPercentage = (logValue / logMax) * 100;
      const percentage = minWidthPercentage +
        (rawPercentage * (100 - minWidthPercentage) / 100);

      return `${Math.max(percentage, minWidthPercentage)}%`;
    },
    getBalancedBarWidth(value) {
      if (this.maxValue <= 0) return '100%';
      if (value <= 0) return this.getMinWidthPercentage() + '%';

      const minWidthPercentage = this.getMinWidthPercentage();
      const exponent = 0.6;
      const adjustedValue = Math.pow(value, exponent);
      const adjustedMax = Math.pow(this.maxValue, exponent);
      const rawPercentage = (adjustedValue / adjustedMax) * 100;
      const percentage = minWidthPercentage +
        (rawPercentage * (100 - minWidthPercentage) / 100);

      return `${Math.max(percentage, minWidthPercentage)}%`;
    },
    getSquareRootBarWidth(value) {
      if (this.maxValue <= 0) return '100%';
      if (value <= 0) return this.getMinWidthPercentage() + '%';

      const minWidthPercentage = this.getMinWidthPercentage();
      const sqrtValue = Math.sqrt(value);
      const sqrtMax = Math.sqrt(this.maxValue);
      const rawPercentage = (sqrtValue / sqrtMax) * 100;
      const percentage = minWidthPercentage +
        (rawPercentage * (100 - minWidthPercentage) / 100);

      return `${Math.max(percentage, minWidthPercentage)}%`;
    },
    calculateAffix(type) {
      const affixes = {
        amount: { prefix: '₴', suffix: '' },
        earnings: { prefix: '₴', suffix: '' },
        count: { prefix: '', suffix: ' ШТ' },
      };

      return affixes[type] || { prefix: '', suffix: '' };
    },
    paginate(page) {
      this.currentPage = page;
      this.$emit('update-slider', 200);
    },
  },
};
</script>

<style lang="scss" scoped>
.partners-chart {
  position: relative;
  width: 100%;
  min-height: 520px;
  padding-bottom: 70px;

  &-container {
    padding: 16px 0 0;
  }

  &__title {
    margin-bottom: 12px;
    padding: 0 20px;
    font-size: 15px;
    font-weight: 400;
  }

  .partners-limit {
    font-size: 15px;
    font-weight: 400;
    color: #949494;
  }

  &__chart {
    width: 100%;
  }

  &__chart-bars {
    padding: 0 20px;
    border-bottom: 1px solid #E9EAEB;
  }

  &__chart-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #0a77ff;
    border-radius: 0 8px 8px 0;
    padding: 7px 16px;
    margin-bottom: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    min-width: 200px;
    transition: width 0.3s ease-in-out;
    white-space: nowrap;
    flex-shrink: 0;
    max-width: 100%;

    @media (max-width: 1050px) {
      flex-direction: column;
      align-items: start;
    }
  }

  .partner-name {
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 10px;
  }

  .partner-sales {
    flex-shrink: 0;
  }

  &-paginator {
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
